import { useContext, useEffect } from 'react';

import { ONBOARDING_PERSONALIZED_TEMPLATES_V2 } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useOnboardingNewHomeTakeoverEnabled } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingNewHomeTakeover.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsEligibleForPersonalizedTemplatesV2 } from '@confluence/experiment-personalized-templates-v2/entry-points/useIsEligibleForPersonalizedTemplatesV2.ts';
import { useIsEligibleForNthUserHomeOnboardingRevamp } from '@confluence/experiment-personalized-templates-v2/entry-points/useIsEligibleForNthUserHomeOnboardingRevamp.ts';
import { useIsEligibleForUseCasesOnboardingHomeChanges } from '@confluence/experiment-personalized-templates-v2/entry-points/useIsEligibleForUseCasesOnboardingHomeChanges.ts';

import { useNthUserHomeOnboardingTakeoverV2Eligibility } from './useNthUserHomeOnboardingTakeoverV2Eligibility.ts';

export type OnboardingTakeoverType =
	| 'v1'
	| 'nth-user'
	| 'personalized-templates-v2'
	| 'personalized-templates-v2-xflow'
	| 'nth-user-home-onboarding-revamp'
	| 'use-cases-in-nth-user-onboarding';
/**
 * Hook to determine which onboarding takeover will be displayed.
 *
 * @returns The type of onboarding takeover, or `null` if no takeover.
 */
export function useOnboardingTakeoverType(): {
	onboardingTakeoverType: OnboardingTakeoverType | null;
	loading?: boolean;
} {
	const { isEnabled: isV1Enabled, loading: isV1Loading } = useOnboardingNewHomeTakeoverEnabled();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		isEligible: v2NthUserEligible,
		loading: v2NthUserLoading,
		isVisible: v2NthUserVisible,
	} = useNthUserHomeOnboardingTakeoverV2Eligibility();

	const {
		isEligibleForLand: ptv2Eligible,
		isEligibleForXflow: ptv2EligibleXflow,
		loading: ptv2Loading,
		isVisible: isPTV2Visible,
		wasPTV2TakeoverSeen,
	} = useIsEligibleForPersonalizedTemplatesV2();

	const {
		isEligible: nthUserHomeOnboardingRevampEligible,
		loading: nthUserHomeOnboardingRevampLoading,
		isVisible: nthUserHomeOnboardingRevampVisible,
	} = useIsEligibleForNthUserHomeOnboardingRevamp();

	const { loading: useCasesLoading, isEligible: isEligibleForUseCasesHomeChanges } =
		useIsEligibleForUseCasesOnboardingHomeChanges();

	const defaultLandFoundingAdminExperience = !isV1Loading && isV1Enabled;

	useEffect(() => {
		if (!ptv2Loading && !(ptv2Eligible || ptv2EligibleXflow)) {
			experienceTracker.abort({
				name: ONBOARDING_PERSONALIZED_TEMPLATES_V2,
				reason: 'Not eligible for v2 takeover',
			});
		}
	}, [ptv2Eligible, ptv2EligibleXflow, ptv2Loading, experienceTracker]);

	if (
		!nthUserHomeOnboardingRevampLoading &&
		nthUserHomeOnboardingRevampEligible &&
		expValEquals('cc-nth-user-home-onboarding-revamp', 'cohort', 'experiment')
	) {
		if (nthUserHomeOnboardingRevampVisible) {
			return { onboardingTakeoverType: 'nth-user-home-onboarding-revamp' };
		}

		return { onboardingTakeoverType: null };
	}

	if (isV1Loading || ptv2Loading || nthUserHomeOnboardingRevampLoading || useCasesLoading) {
		return { onboardingTakeoverType: null, loading: true };
	}

	if (isEligibleForUseCasesHomeChanges) {
		return { onboardingTakeoverType: 'use-cases-in-nth-user-onboarding' };
	}

	if (
		ptv2Eligible &&
		expValEquals('cc-onboarding-personalized-templates-v2', 'cohort', 'experiment')
	) {
		return isPTV2Visible
			? { onboardingTakeoverType: 'personalized-templates-v2' }
			: { onboardingTakeoverType: null };
	} else if (
		ptv2EligibleXflow &&
		expValEquals('cc-activ-personalized-templates-v2-xflow', 'cohort', 'experiment')
	) {
		return isPTV2Visible
			? { onboardingTakeoverType: 'personalized-templates-v2-xflow' }
			: { onboardingTakeoverType: null };
	} else if (defaultLandFoundingAdminExperience && !wasPTV2TakeoverSeen) {
		return { onboardingTakeoverType: 'v1' };
	}

	if (!v2NthUserLoading && v2NthUserEligible) {
		return v2NthUserVisible
			? { onboardingTakeoverType: 'nth-user' }
			: { onboardingTakeoverType: null };
	}

	return { onboardingTakeoverType: null };
}
